import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  closeAssignModal,
  fetchSectionList,
  assignAssetToSections,
} from '@/features/sections/sectionsSlice';
import { showToast } from '@/features/ui/uiSlice';
import styles from './AssignSectionModal.module.css';

export default function AssignSectionModal() {
  const dispatch = useAppDispatch();
  const assetId = useAppSelector((s) => s.sections.assignModalAssetId)!;
  const sections = useAppSelector((s) => s.sections.sections);
  const sectionsLoading = useAppSelector((s) => s.sections.sectionsLoading);
  const assignLoading = useAppSelector((s) => s.sections.assignLoading);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Load section list on open if not already loaded
  useEffect(() => {
    if (sections.length === 0) dispatch(fetchSectionList());
  }, [dispatch, sections.length]);

  const handleClose = () => dispatch(closeAssignModal());

  const toggleSection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    const result = await dispatch(
      assignAssetToSections({ assetId, payload: { section_ids: selectedIds } })
    );
    if (assignAssetToSections.fulfilled.match(result)) {
      const count = (result.payload as { assigned: number }).assigned;
      dispatch(
        showToast({
          title: 'Added to collections',
          message: `Asset added to ${count} collection${count !== 1 ? 's' : ''}.`,
        })
      );
    }
  };

  // Only show non-dynamic sections (dynamic sections reject manual assignment)
  const assignableSections = sections.filter((s) => !s.is_dynamic);

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="assign-section-title">
        {/* Header */}
        <div className={styles.header}>
          <span id="assign-section-title" className={styles.headerTitle}>Add to Collection</span>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {sectionsLoading ? (
            <p className={styles.loading}>Loading collections…</p>
          ) : assignableSections.length === 0 ? (
            <p className={styles.empty}>No collections yet. Create one first.</p>
          ) : (
            assignableSections.map((section) => (
              <label key={section.id} className={styles.sectionItem}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedIds.includes(section.id)}
                  onChange={() => toggleSection(section.id)}
                />
                {/* Color dot */}
                <span
                  className={styles.colorDot}
                  style={{
                    background: section.color ?? 'rgba(255,255,255,0.2)',
                    border: section.color ? 'none' : '1px solid rgba(255,255,255,0.15)',
                  }}
                />
                <span className={styles.sectionLabel}>{section.label}</span>
                <span className={styles.sectionCount}>{section.asset_count}</span>
              </label>
            ))
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} onClick={handleClose}>Cancel</button>
          <button
            type="button"
            className={styles.assignBtn}
            disabled={assignLoading || selectedIds.length === 0}
            onClick={handleAssign}
          >
            {assignLoading ? 'Adding…' : `Add to ${selectedIds.length || ''} collection${selectedIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
